import React from 'react'
import dayjs from 'dayjs'
import {
  Card,
  Avatar,
  Typography,
  CircularProgress,
  Box,
  CardProps,
  useTheme
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { PATIENT_HEALTH_DATA } from 'navigation/CONSTANTS'
import WarningIcon from '@mui/icons-material/Warning'
import NavLinkBase from 'design-system/NavLink'
import VitalSignSummary from './components/VitalSignSummary'
import MilestoneItem from './components/MilestoneItem'
import { IPatientVitalSignsResponse } from 'types/patient/types'
import { useBannerData, useShowBannerHDA } from 'utils/hda'

import 'dayjs/locale/pt-br'
import { IPatientMilestonesResponse } from 'types'

dayjs.locale('pt-br')

const MOCK_MILESTONES: { key: keyof IPatientMilestonesResponse; label: string; icon: string }[] = [
  {
    key: 'installation_date',
    label: 'Instalação',
    icon: 'build',
  },
  {
    key: 'surgery_date',
    label: 'Cirurgia',
    icon: 'vaccines',
  },
  {
    key: 'calibration_date',
    label: 'Calibração',
    icon: 'settings',
  },
  {
    key: 'return_date',
    label: 'Retorno',
    icon: 'replay',
  }
]

interface IPatientCardProps extends CardProps {
  patientId: string
  name: string
  email: string
  age: number | null
  imageUrl: string
  overallHealth: number
  overallHealthWarning: number
  overallHealthColor: string
  vitalSigns?: IPatientVitalSignsResponse
  milestones?: IPatientMilestonesResponse
  onClick?:
    | React.MouseEventHandler<HTMLAnchorElement | HTMLDivElement>
    | undefined
  preventDefault?: boolean
}

interface IOverallWarningProps {
  showAlert: boolean
  showBannerHda: boolean
  overallHealth: number
  overallHealthColor: string
}

const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  padding: theme.spacing(2),
  borderRadius: '12px',
  gap: theme.spacing(1.5),
  transition: 'transform 0.2s ease-in-out',
  height: '100%',
  marginBottom: '0!important',
  '&:hover': {
    transform: 'scale(1.01)'
  }
}))

const PatientInfoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  flex: 'none'
}))

const PatientBasicInfo = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  flex: 1
}))

const VitalSignsSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
  minHeight: '32px'
}))

const MilestoneSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(2),
  rowGap: theme.spacing(2),
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
  minHeight: '32px',
  marginTop: theme.spacing(1),
  "@media (min-width: 900px) and (max-width: 1199px), (max-width: 630px)": {
    "& > div": {
      flexBasis: '40%'
    },
    justifyContent: 'flex-start',
  },
}))

const HealthIndicatorSection = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}))

const OverallWarningComponent: React.FC<IOverallWarningProps> = ({
  showAlert,
  showBannerHda,
  overallHealth,
  overallHealthColor
}) => {
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex'
      }}
    >
      {showAlert ||
      (overallHealth != 0 && overallHealth != null && overallHealth <= 75) ? (
        <WarningIcon
          sx={{
            color: showBannerHda
              ? 'hdaWarning.main'
              : `rgba(${overallHealthColor})`,
            fontSize: '50px'
          }}
        />
      ) : (
        <>
          <CircularProgress
            variant='determinate'
            value={showBannerHda ? undefined : overallHealth}
            thickness={3}
            size='50px'
            sx={{
              color: `rgba(${overallHealthColor})`
            }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {!showBannerHda && (
              <Typography
                variant='caption'
                component='div'
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: 'text.secondary'
                }}
              >
                {overallHealth}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}

const PatientCard: React.FC<IPatientCardProps> = ({
  patientId,
  name,
  email,
  age,
  imageUrl,
  vitalSigns,
  milestones,
  overallHealth,
  overallHealthColor,
  overallHealthWarning,
  onClick,
  preventDefault,
  ...props
}) => {
  const theme = useTheme()
  const showBannerHda = useShowBannerHDA()
  const bannerData = useBannerData(vitalSigns)

  const hasBannerDataToList = vitalSigns ? bannerData.length > 0 : false
  const showAlert = showBannerHda && hasBannerDataToList

  const renderVitalSigns = () => {
    if (!vitalSigns) return null

    const signs = []

    if (vitalSigns.heart_rate) {
      signs.push(
        <VitalSignSummary
          key='heart_rate'
          icon={<span className='material-symbols-outlined'>ecg_heart</span>}
          value={vitalSigns.heart_rate.toString() + 'bpm'}
          background={theme.palette.vitalSignHeartRate.main}
          title='Frequência Cardíaca'
        />
      )
    }

    if (vitalSigns.o2_saturation) {
      signs.push(
        <VitalSignSummary
          key='o2_saturation'
          icon={<span className='material-symbols-outlined'>spo2</span>}
          value={vitalSigns.o2_saturation.toString() + '%'}
          background={theme.palette.vitalSignO2Saturation.main}
          title='Saturação de O2'
        />
      )
    }

    if (
      vitalSigns.blood_pressure_systolic &&
      vitalSigns.blood_pressure_diastolic
    ) {
      signs.push(
        <VitalSignSummary
          key='blood_pressure'
          icon={
            <span className='material-symbols-outlined'>blood_pressure</span>
          }
          value={`${vitalSigns.blood_pressure_systolic}/${vitalSigns.blood_pressure_diastolic}mmHg`}
          background={theme.palette.vitalSignBloodPressure.main}
          title='Pressão Arterial'
        />
      )
    }

    return signs
  }

  const renderMilestones = () => {
    if (!milestones) return null
    const milestonesData: React.JSX.Element[] = []
    MOCK_MILESTONES.forEach((milestone, index) => {
    // for (const milestone of MOCK_MILESTONES) {
      const date = milestones[milestone.key]
      if (date) {
        milestonesData.push(
          <React.Fragment key={milestone.key}>
            <MilestoneItem
              icon={milestone.icon}
              label={milestone.label}
              date={dayjs(date).format('DD/MM/YYYY')}
              color={theme.palette.text.primary}
            />
          </React.Fragment>
        )
      }
    })

    return milestonesData
  }

  const Container: React.FC<React.PropsWithChildren> = ({ children }) => {
    if (preventDefault) {
      return (
        <Box
          sx={{
            cursor: onClick ? 'pointer' : undefined
          }}
          onClick={onClick}
        >
          {children}
        </Box>
      )
    }
    return (
      <NavLinkBase
        onClick={onClick}
        to={!onClick ? PATIENT_HEALTH_DATA(patientId) : '#'}
        absolute
      >
        {children}
      </NavLinkBase>
    )
  }

  return (
    <Container>
      <StyledCard {...props}>
        <PatientInfoSection>
          <PatientBasicInfo>
            <Avatar
              src={imageUrl}
              alt={name}
              sx={{
                width: 48,
                height: 48,
                marginRight: 2
              }}
            />
            <Box textAlign='left'>
              <Typography
                variant='body1'
                sx={{
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                {name}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  fontSize: '0.875rem',
                  color: 'text.secondary'
                }}
              >
                {age ? age + ' anos' : ''}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  fontSize: '0.875rem',
                  color: 'text.secondary'
                }}
              >
                {email}
              </Typography>
            </Box>
          </PatientBasicInfo>

          <HealthIndicatorSection>
            <OverallWarningComponent
              showAlert={showAlert}
              showBannerHda={showBannerHda}
              overallHealth={overallHealth}
              overallHealthColor={overallHealthColor}
            />
          </HealthIndicatorSection>
        </PatientInfoSection>

        <VitalSignsSection>{renderVitalSigns()}</VitalSignsSection>
        {showBannerHda && (
          <MilestoneSection>{renderMilestones()}</MilestoneSection>
        )}
      </StyledCard>
    </Container>
  )
}

export default PatientCard
